<template>
	<section class="performance-dashboard">
		<!-- Header Section -->
		<div class="dashboard-header">
			<div class="header-content">
				<h2 class="dashboard-title">{{ $t('performance_overview') }}</h2>
				<div class="dashboard-actions">
					<button class="refresh-btn" @click="refreshData" :disabled="isLoading" aria-label="Refresh data">
						<span v-if="isLoading">{{ $t('loading') }}...</span>
						<span v-else>{{ $t('refresh') }}</span>
					</button>
					<button class="export-btn" @click="exportData" aria-label="Export data">
						<span>{{ $t('export') }}</span>
					</button>
				</div>
			</div>
		</div>

		<!-- Filter Panel -->
		<div class="filter-section">
			<div class="filter-header">
				<h3>{{ $t('metrics') }}</h3>
				<span class="filter-subtitle">{{ $t('select_three_metrics') }}</span>
			</div>
			<ChartFilterPanel
				:buttons="chartFilterPanelData.buttons"
				@input="handleFilterClick"
				:max-selections="4"
				class="enhanced-filter-panel"
			/>
		</div>

		<!-- Chart Section -->
		<div class="chart-section">
			<!-- Chart Header with Y-axis Labels -->
			<div class="chart-header" v-if="chartTitles.length > 0">
				<div class="chart-y-titles">
					<div
						v-for="(title, index) in chartTitles"
						:key="index"
						class="y-axis-label"
						:style="{ '--label-color': title.color }"
					>
						<span class="color-indicator"></span>
						{{ $t(title.text) }}
					</div>
				</div>
			</div>

			<!-- Chart Container -->
			<div class="chart-container">
				<!-- Loading State -->
				<div v-if="isLoading" class="loading-container">
					<div class="loading-spinner">
						<div class="spinner"></div>
						<p>{{ $t('loading_chart') }}</p>
					</div>
				</div>

				<!-- No Data State -->
				<div v-else-if="!isData" class="no-data-container">
					<div class="no-data-content">
						<h3>{{ $t('no_data_available') }}</h3>
						<p>{{ $t('no_data_available') }}</p>
						<button class="retry-btn" @click="refreshData">
							{{ $t('retry') }}
						</button>
					</div>
				</div>

				<!-- Chart Display -->
				<div v-else-if="chartData.datasets && chartData.datasets[0]" class="chart-wrapper">
					<!-- Interactive Chart -->
					<Chart
						class="interactive-chart"
						type="bar"
						:labels="chartData.labels"
						:datasets="chartData.datasets"
						:height="chartHeight"
						:options="enhancedChartOptions"
						@click="handleChartClick"
						:isClickable="true"
						:aria-label="chartAriaLabel"
					/>
				</div>
			</div>

			<!-- Chart Footer with Insights -->
			<div class="chart-footer" v-if="!isLoading && isData">
				<div class="chart-insights">
					<div class="insight-item positive" v-if="hasPositiveTrend">
						<span>📈 {{ $t('performance_trending_upward') }}</span>
					</div>
					<div class="insight-item warning" v-if="hasHighComplaintRatio">
						<span>⚠️ {{ $t('high_complaint_ratio') }}</span>
					</div>
				</div>
				<div class="chart-interactions">
					<p class="interaction-hint">💡 {{ $t('click_bars_for_details') }}</p>
				</div>
			</div>
		</div>
	</section>
</template>

<script>
import Chart from '@/modules/common/components/Chart'
import { mapGetters } from 'vuex'
import { buttonsInfo, overviewChartOptions } from '../consts/consts'
import ChartFilterPanel from './ChartFilterPanel'
import { getOverviewSums, getAnalyticsSums, getOverviewUnnecessaryArrivals, isUnnecessaryArrivalsShown, isMetricShown, getUnnecessaryArrivalsShareText } from '../services/performanceService'
import performanceService from '../services/performanceService'
import performanceChartService from '../services/performanceChartService'
import util from '../../common/services/utilService'
import { Toast } from '@/modules/common/Toast'
import { trendArrowColors } from '../consts/consts'

export default {
	name: 'PerformanceDashboard',

	created() {
		// Setup watchers - Dashboard handles all fetching
		this.setupWatchers()
		
		// Initial render from existing store data (if any)
		const storeOverview = this.$store.getters.overviewData
		const storeAnalytics = this.$store.getters.analyticsData
		if (storeOverview && storeAnalytics && !this.isLoading) {
			this.updatePageData()
		}
	},

	data() {
		return {
			chartTitles: [],
			chartFilterPanelData: {
				buttons: JSON.parse(JSON.stringify(buttonsInfo))
			},
			chartFilter: {
				avg_score: true,
				visits: false,
				complaints: false
			},
			chartData: {},
			chartOptions: overviewChartOptions,
			isShownTicks: true,
			isLoading: false,
			isData: false,
			chartHeight: '400px',
			lastRefresh: null
		}
	},

	computed: {
		...mapGetters(['overviewData', 'analyticsData', 'filterSelected']),

		roomId() {
			return this.$route.params.roomId
		},

		enhancedChartOptions() {
			return {
				...this.chartOptions,
				scales: {
					...this.chartOptions.scales,
					xAxes: this.getEnhancedXAxisOptions()
				},
				responsive: true,
				maintainAspectRatio: false,
				plugins: {
					legend: {
						display: false // We show custom legend
					},
					tooltip: {
						backgroundColor: 'rgba(0, 0, 0, 0.8)',
						titleColor: '#fff',
						bodyColor: '#fff',
						borderColor: 'rgba(255, 255, 255, 0.1)',
						borderWidth: 1,
						cornerRadius: 8,
						displayColors: true
					}
				},
				interaction: {
					mode: 'index',
					intersect: false
				},
				animation: {
					duration: 750,
					easing: 'easeInOutQuart'
				}
			}
		},

		chartAriaLabel() {
			const activeMetrics = this.chartFilterPanelData.buttons
				.filter((btn) => btn.isActive)
				.map((btn) => btn.name)
				.join(', ')
			return `Performance chart showing ${activeMetrics} over time`
		},

		hasPositiveTrend() {
			return this.chartFilterPanelData.buttons.some((btn) => btn.isActive && btn.trendInfo?.upDown === 'up')
		},

		hasHighComplaintRatio() {
			const complaintsBtn = this.chartFilterPanelData.buttons.find((btn) => btn.name === 'complaints_vs_visits')
			return complaintsBtn?.isActive && parseFloat(complaintsBtn.text) > 5
		}
	},

	methods: {
		getEnhancedXAxisOptions() {
			const existingXAxes = this.chartOptions.scales && this.chartOptions.scales.xAxes
				? this.chartOptions.scales.xAxes
				: [{}]

			return existingXAxes.map((axis) => ({
				...axis,
				ticks: {
					...(axis.ticks || {}),
					callback: (label) => this.formatDateAxisLabel(label),
					maxRotation: 0,
					autoSkip: true
				}
			}))
		},

		formatDateAxisLabel(label) {
			if (this.filterSelected.viewTimeBy !== 'day' || typeof label !== 'string') {
				return label
			}

			const date = this.getDateFromChartLabel(label)
			if (!date) return label

			const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
			const dayName = this.$t(dayKeys[date.getDay()])
			return [dayName, label]
		},

		getDateFromChartLabel(label) {
			const parts = label.split('/').map((part) => parseInt(part, 10))
			if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
				return null
			}

			const [day, month, labelYear] = parts
			const year = labelYear < 100 ? 2000 + labelYear : labelYear
			const date = new Date(year, month - 1, day)

			if (
				date.getFullYear() !== year ||
				date.getMonth() !== month - 1 ||
				date.getDate() !== day
			) {
				return null
			}

			return date
		},

		setupWatchers() {
			// Use a flag to prevent duplicate calls
			let isUpdating = false;
			
			// Watch for when both overviewData and analyticsData are available
			// Only trigger updatePageData once when both are ready
			this.$store.watch(
				(state) => ({
					overviewData: this.overviewData,
					analyticsData: this.analyticsData
				}),
				async ({ overviewData, analyticsData }) => {
					if (overviewData && analyticsData && !isUpdating) {
						isUpdating = true;
						try {
							await this.updatePageData();
						} finally {
							isUpdating = false;
						}
					} else if (!overviewData || !analyticsData) {
						// Store was cleared, reset local state
						this.chartData = {}
						this.isData = false
						this.chartFilterPanelData = {
							buttons: JSON.parse(JSON.stringify(buttonsInfo))
						}
					}
				},
				{ deep: false }
			)
		},

		async refreshData() {
			this.isLoading = true
			this.lastRefresh = new Date()

			try {
				// Always use latest filterSelected from store, not cached computed value
				const latestFilter = this.$store.getters.filterSelected;
				const filter = !this.roomId ? { ...latestFilter } : { ...latestFilter, roomId: this.roomId }

				await Promise.all([
					this.$store.dispatch({ type: 'getOverviewData', filter }),
					this.$store.dispatch({ type: 'getAnalyticsData', filter })
				])
			} catch (error) {
				new Toast({
					message: this.$t('refresh_failed'),
					type: 'error',
					duration: 5000
				})
			} finally {
				this.isLoading = false
			}
		},

		async exportData() {
			try {
				// Create a simple CSV export of the chart data
				const csvContent = this.generateCSV()

				// Create and download the file
				const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
				const link = document.createElement('a')

				if (link.download !== undefined) {
					const url = URL.createObjectURL(blob)
					link.setAttribute('href', url)
					link.setAttribute('download', `performance-data-${new Date().toISOString().split('T')[0]}.csv`)
					link.style.visibility = 'hidden'
					document.body.appendChild(link)
					link.click()
					document.body.removeChild(link)
					URL.revokeObjectURL(url)
				}

			new Toast({
				message: this.$t('data_exported_successfully'),
				type: 'success',
				duration: 3000
			})
			} catch (error) {
			new Toast({
				message: this.$t('data_export_failed'),
				type: 'error',
				duration: 4000
			})
			}
		},

		generateCSV() {
			if (!this.chartData || !this.chartData.labels) {
				return this.$t('no_data_available')
			}

			const dateHeader = `${this.$t('date')}`
			const headers = [dateHeader, ...this.chartData.datasets.map((dataset) => dataset.label)].filter(
				(label) => !label.includes('trendline')
			)
			const csvRows = [headers.join(',')]

			this.chartData.labels.forEach((label, index) => {
				const row = [label]
				this.chartData.datasets.forEach((dataset) => {
					if (dataset.label.includes('trendline')) return
					row.push(dataset.data[index] || 0)
				})
				csvRows.push(row.join(','))
			})

			return csvRows.join('\n')
		},
	async handleChartClick({ label }) {
		if (this.filterSelected.viewTimeBy !== 'day' || !label.split('').includes('/')) {
			new Toast({
				message: this.$t('drill_down_unavailable'),
				type: 'info',
				duration: 4000
			})
			return
		}

		try {
			const labelYear = label.split('/').slice(-1)[0]
			const clickedLabelYear = labelYear.length < 4 ? `20${labelYear}` : labelYear

			label = label.split('/').slice(0, -1).join('/')
			const splitted = label.split('')
			const middleIndex = splitted.findIndex((a) => a === '/')
			const dayPart = splitted.slice(0, middleIndex)
			if (dayPart.length < 2) dayPart.unshift('0')
			const monthPart = splitted.slice(middleIndex + 1, splitted.length)
			if (monthPart.length < 2) monthPart.unshift('0')

			// Use the clicked label's year for both timeFrom and timeTo
			const timeFrom = [
				clickedLabelYear,
				'-',
				...monthPart,
				'-',
				...dayPart
			].join('')

			// timeTo is the same as timeFrom since we're filtering for a single day
			const timeTo = timeFrom

		// Set global loading state before updating filter (prevents stale data flash)
		this.$store.commit({ type: 'toggleLoading', value: true })
		
		// Update store with new time filter - Dashboard's watcher will handle the fetch
		this.$store.commit({
			type: 'updateFilter',
			field: 'timeType',
			value: 'custom_time'
		})
		this.$store.commit({
			type: 'updateFilter',
			field: 'time',
			value: { timeFrom, timeTo }
		})

		// Navigate to appropriate detailed view
		if (this.$route.name === 'performance') {
			this.$router.push({ name: 'analytics' })
		} else if (this.$route.name === 'roomAnalytics') {
			this.$router.push({ name: 'cleanLog' })
		}

		new Toast({
			message: `Filtered to ${label}`,
			type: 'success',
			duration: 3000
		})
	} catch (error) {
		new Toast({
			message: 'Failed to filter data',
			type: 'error',
			duration: 4000
		})
		// Clear loading state on error
		this.$store.commit({ type: 'toggleLoading', value: false })
	}
	},

		handleFilterClick(index) {
			let activeButtonCount = 0
			this.chartFilterPanelData.buttons.forEach((button) => {
				return button.isActive ? (activeButtonCount += 1) : ''
			})

			if (activeButtonCount >= 4 && !this.chartFilterPanelData.buttons[index].isActive) {
				new Toast({
					message: this.$t('select_four_metrics'),
					type: 'warning',
					duration: 4000
				})
				return
			}

			this.chartFilterPanelData.buttons[index].isActive = !this.chartFilterPanelData.buttons[index].isActive

			this.chartFilterPanelData.buttons[index].isActive ? (activeButtonCount += 1) : (activeButtonCount -= 1)

			activeButtonCount === 4 ? (this.isShownTicks = false) : (this.isShownTicks = true)

			this.chartFilterPanelData.buttons.forEach((button) => (this.chartFilter[button.name] = button.isActive))

			this.updateChart(this.chartFilter)
		},

		async updatePageData() {
			if (!this.analyticsData || !this.overviewData) return

			// Check if we have any data
			let isData = false
			for (let key in this.overviewData) {
				if (Array.isArray(this.overviewData[key]) && this.overviewData[key].length) {
					isData = true
					break
				}
			}
			this.isData = isData

			if (!isData) return

		const overviewSums = getOverviewSums(this.overviewData)
		const analyticsSums = getAnalyticsSums(this.analyticsData.hourData)
		if (this.analyticsData.roomsData && this.analyticsData.roomsData.length) {
			analyticsSums.visits = this.analyticsData.roomsData.reduce((sum, r) => sum + (Number(r.visits) || 0), 0)
		}
		// Always use trendInfo from store - it's computed when overviewData is updated via computeTrendInfo
		// The store ensures trendInfo is computed once and cached in Vuex state
		// Add null check to prevent errors when switching companies (trendInfo might be null temporarily)
		const trendInfo = this.$store.getters.trendInfo || {};

		const unnecessaryArrivals = getOverviewUnnecessaryArrivals(overviewSums)
		const isUnnecessaryArrivalsButtonShown = isUnnecessaryArrivalsShown(unnecessaryArrivals, this.filterSelected?.servikName)
		if (!isUnnecessaryArrivalsButtonShown) this.chartFilter.unnecessary_arrivals = false

		this.updateChart(this.chartFilter)

			// Start from every button, so a button hidden by an earlier filter shows again once its value returns
			this.chartFilterPanelData = {
				...overviewSums,
				...analyticsSums,
				buttons: JSON.parse(JSON.stringify(buttonsInfo)).map((buttonInfo) => ({
					...buttonInfo,
					isActive: !!this.chartFilter[buttonInfo.name]
				}))
			}

			this.chartFilterPanelData.buttons = this.chartFilterPanelData.buttons.reduce((acc, buttonInfo) => {
				const isShown = isMetricShown(
					buttonInfo.name,
					this.chartFilterPanelData[buttonInfo.name],
					unnecessaryArrivals,
					this.filterSelected?.servikName
				)
				if (isShown) {
					// Safely access trendInfo property with null check
					buttonInfo.trendInfo = trendInfo && trendInfo[buttonInfo.name] ? trendInfo[buttonInfo.name] : null;

					if (buttonInfo.trendInfo) {
						buttonInfo.trendInfo.color = trendArrowColors[buttonInfo.name]?.[buttonInfo.trendInfo.upDown]
					}

					const currButtonValue = this.chartFilterPanelData[buttonInfo.name]

					// Format button text based on value and type
					this.formatButtonText(buttonInfo, currButtonValue)

					// Set amendment flag for visits
					if (buttonInfo.name === 'visits') {
						buttonInfo.isAmended = this.chartFilterPanelData.isAmended
					}

					acc.push(buttonInfo)
				}
				return acc
			}, [])
		},

		formatButtonText(buttonInfo, value) {
			try {
				if (value < 1000 && (buttonInfo.name === 'response_time_SLA' || buttonInfo.name === 'complaints')) {
					buttonInfo.text = `${new Number(value)}`
				} else {
					buttonInfo.text = `${value >= 1000 ? util.kFormatter(value) : new Number(value)}`

					if (value >= 1000 && buttonInfo.name === 'time_saved') {
						buttonInfo.text = `${util.kMinutesToHours(value)}`
					}
				}

				// Add specific formatting for different metrics
				if (buttonInfo.name === 'avg_score') {
					buttonInfo.text = new Number(value).toFixed(1)
				} else if (buttonInfo.name === 'response_time_SLA') {
					buttonInfo.text += ` ${this.$t('min')}`
				} else if (buttonInfo.name === 'time_saved') {
					buttonInfo.text += value >= 1000 ? '' : ` ${this.$t('min')}`
				} else if (buttonInfo.name === 'complaints_vs_visits') {
					buttonInfo.text += '%'
				} else if (buttonInfo.name === 'unnecessary_arrivals') {
					buttonInfo.subText = getUnnecessaryArrivalsShareText({
						count: value,
						checkedArrivals: this.chartFilterPanelData.unnecessary_arrivals_checked,
					}, this.$t('of_arrivals'))
					buttonInfo.hint = this.$t('unnecessary_arrivals_hint')
				}
			} catch (err) {
				console.warn('Error formatting button text:', err)
			}
		},

		updateChart(filter) {
			let chartData = performanceChartService.getOverviewChartData(this.overviewData, filter)

			if (this.filterSelected.viewTimeBy === 'month') {
				chartData.labels = chartData.labels.map((label) => {
					const [month, year] = label.split('/')
					return `${this.$t(`monthMap.${month}`)} ${year}`
				})
			}

			const { datasetsWithStyle, chartTitles, yAxesOptions } = performanceChartService.getChartStyles(
				chartData,
				this.isShownTicks
			)

			this.chartTitles = chartTitles

			// Apply gridlines only to the first y-axis
			yAxesOptions.forEach((axis, index) => {
				if (index === 0) {
					// Only apply gridlines to the first axis
					axis.gridLines.zeroLineWidth = 0
					axis.gridLines.color = 'rgba(0, 0, 0, 0.05)'
				}
				// Apply common styling to all axes
				axis.ticks.fontColor = '#666'
				axis.ticks.fontSize = 12
			})

			// Replace `scales` (a reactive property) so the `enhancedChartOptions`
			// computed re-evaluates. Mutating `scales.yAxes` directly would not
			// notify dependents because `yAxes` was never on the original
			// `overviewChartOptions.scales` object Vue observed, so it has no
			// reactive setter and dataset yAxisIDs would point to stale scale ids.
			this.chartOptions.scales = {
				...this.chartOptions.scales,
				yAxes: yAxesOptions
			}
			chartData.datasets = datasetsWithStyle
			this.chartData = chartData
		}
	},

	components: {
		ChartFilterPanel,
		Chart
	}
}
</script>

<style lang="scss" scoped>
@use '@/styles/mixins' as *;

.performance-dashboard {
	border-radius: 12px;
	padding: 8px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.dashboard-header {
	margin-bottom: 16px;

	.header-content {
		display: flex;
		justify-content: space-between;
		align-items: center;
		flex-wrap: wrap;
		gap: 16px;
	}

	.dashboard-title {
		font-size: 1.75rem;
		font-weight: 600;
		color: #1f2937;
		margin: 0;
	}

	.dashboard-actions {
		display: flex;
		gap: 12px;
	}

	.refresh-btn,
	.export-btn {
		@include flex-center;
		gap: 8px;
		padding: 10px 12px;
		border: 1px solid #d1d5db;
		border-radius: 8px;
		font-size: 14px;
		cursor: pointer;
		transition: all 0.2s ease;
		min-width: 80px;

		&:hover {
			background: #f9fafb;
			border-color: #9ca3af;
		}

		&:disabled {
			opacity: 0.5;
			cursor: not-allowed;
		}
	}

	.export-btn {
		background: #3b82f6;
		color: white;
		border-color: #3b82f6;

		&:hover {
			background: #2563eb;
			border-color: #2563eb;
		}
	}
}

.filter-section {
	border-radius: 12px;
	padding: 24px;
	margin-bottom: 24px;
	border: 1px solid #e5e7eb;

	.filter-header {
		margin-bottom: 20px;

		h3 {
			font-size: 1.25rem;
			font-weight: 600;
			color: #1f2937;
			margin: 0 0 4px 0;
		}

		.filter-subtitle {
			color: #6b7280;
			font-size: 14px;
		}
	}
}

.chart-section {
	background: white;
	border-radius: 12px;
	padding: 24px;
	border: 1px solid #e5e7eb;

	.chart-header {
		margin-bottom: 20px;

		.chart-y-titles {
			display: flex;
			justify-content: space-between;
			align-items: center;
			flex-wrap: wrap;
			gap: 16px;

			.y-axis-label {
				display: flex;
				align-items: center;
				gap: 8px;
				font-weight: 500;
				font-size: 14px;
				color: var(--label-color, #6b7280);

				.color-indicator {
					width: 12px;
					height: 12px;
					border-radius: 50%;
					background: var(--label-color, #6b7280);
				}
			}
		}
	}

	.chart-container {
		position: relative;
		min-height: 400px;
	}

	.loading-container {
		display: flex;
		align-items: center;
		justify-content: center;
		height: 400px;

		.loading-spinner {
			text-align: center;

			.spinner {
				width: 48px;
				height: 48px;
				border: 4px solid #e5e7eb;
				border-top: 4px solid #3b82f6;
				border-radius: 50%;
				animation: spin 1s linear infinite;
				margin: 0 auto 16px;
			}

			p {
				color: #6b7280;
				font-size: 16px;
				margin: 0;
			}
		}
	}

	.no-data-container {
		display: flex;
		align-items: center;
		justify-content: center;
		height: 400px;

		.no-data-content {
			text-align: center;
			max-width: 400px;

			.icon-chart-empty {
				font-size: 4rem;
				color: #d1d5db;
				margin-bottom: 16px;
			}

			h3 {
				font-size: 1.5rem;
				font-weight: 600;
				color: #374151;
				margin: 0 0 8px 0;
			}

			p {
				color: #6b7280;
				font-size: 16px;
				margin: 0 0 24px 0;
				line-height: 1.5;
			}

			.retry-btn {
				padding: 12px 24px;
				background: #3b82f6;
				color: white;
				border: none;
				border-radius: 8px;
				font-weight: 500;
				cursor: pointer;
				transition: background 0.2s ease;

				&:hover {
					background: #2563eb;
				}
			}
		}
	}

	.chart-wrapper {
		position: relative;

		.interactive-chart {
			transition: opacity 0.3s ease;

			&:hover {
				opacity: 0.95;
			}
		}
	}

	.chart-footer {
		margin-top: 16px;
		padding-top: 16px;
		border-top: 1px solid #e5e7eb;
		display: flex;
		justify-content: space-between;
		align-items: center;
		flex-wrap: wrap;
		gap: 16px;

		.chart-insights {
			display: flex;
			gap: 16px;
			flex-wrap: wrap;

			.insight-item {
				padding: 8px 12px;
				border-radius: 6px;
				font-size: 14px;
				font-weight: 500;

				&.positive {
					background: #ecfdf5;
					color: #059669;
				}

				&.warning {
					background: #fef3c7;
					color: #d97706;
				}
			}
		}

		.chart-interactions {
			.interaction-hint {
				color: #6b7280;
				font-size: 14px;
				margin: 0;
			}
		}
	}
}

.interactive-chart {
	@media print {
		display: none;
	}
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

// Responsive adjustments
@media (max-width: 768px) {
	.performance-dashboard {
		padding: 16px;
		margin: 8px 0;
	}

	.dashboard-header {
		.header-content {
			flex-direction: column;
			align-items: stretch;
		}

		.dashboard-actions {
			justify-content: center;
		}
	}

	.filter-section,
	.chart-section {
		padding: 16px;
	}

	.chart-footer {
		flex-direction: column;
		align-items: stretch;

		.chart-insights {
			justify-content: center;
		}
	}
}

// RTL Support
@include rtl {
	.dashboard-header .header-content {
		flex-direction: row-reverse;
	}

	.chart-y-titles {
		flex-direction: row-reverse;
	}

	.dashboard-actions {
		flex-direction: row-reverse;
	}

	.chart-insights {
		flex-direction: row-reverse;
	}

	.y-axis-label {
		flex-direction: row-reverse;
	}

	.insight-item {
		flex-direction: row-reverse;
	}
}

// High contrast mode support
@media (prefers-contrast: high) {
	.performance-dashboard {
		border: 2px solid #000;
	}

	.filter-section,
	.chart-section {
		border: 1px solid #000;
	}

	.loading-spinner .spinner {
		border-color: #000;
		border-top-color: #0066cc;
	}
}

// Reduced motion support
@media (prefers-reduced-motion: reduce) {
	.spinner {
		animation: none;
	}

	.interactive-chart {
		transition: none;
	}

	.refresh-btn,
	.export-btn,
	.retry-btn {
		transition: none;
	}
}
</style>
